import React, { useState, useEffect, useCallback } from 'react';
import { AñoEscolar } from 'types/usuario.types';
import { UsuarioListItem } from 'types/usuario.types';
import {
  EstadoCuenta, PagoMatriculaResumen, PensionItem, PagarPayload, TipoDescuento, DescuentoAsignado,
  CobroAdicionalResumen,
} from 'types/pago.types';
import { cobroAdicionalService } from 'services/cobrosAdicionalesService';
import { estadoCuentaService, pagoMatriculaService, pensionService, tipoDescuentoService } from 'services/pagoService';
import { HttpError } from 'utils/httpClient';
import { catalogoService, usuarioService } from 'services/usuarioService';
import { useAuth } from 'contexts/AuthContext';
import { imprimirTicket, descargarTicket, TicketData } from 'utils/ticketPrint';
import { getConfig } from 'utils/appConfig';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// CobrosPage – Registro de pagos de matrícula y pensiones
// ============================================================

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_CHECK  = 'M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z';
const D_MONEY  = 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.798 7.45c.512-.67 1.135-1.2 1.703-1.2s1.19.53 1.703 1.2l.127.166a.75.75 0 1 0 1.197-.904l-.127-.166C12.585 5.397 11.536 4.5 10.5 4.5c-1.035 0-2.085.897-2.901 2.046-.394.544-.744 1.183-1.003 1.851A9.6 9.6 0 0 0 6.25 10c0 .61.116 1.194.346 1.703.26.668.61 1.307 1.003 1.851C8.415 14.603 9.465 15.5 10.5 15.5c1.036 0 2.085-.897 2.901-2.046a.75.75 0 1 0-1.197-.904c-.512.67-1.135 1.2-1.703 1.2s-1.19-.53-1.703-1.2c-.32-.418-.607-.915-.8-1.448A8.075 8.075 0 0 1 7.75 10c0-.517.083-1.004.248-1.102.193-.533.48-1.03.8-1.448Z';
const D_X      = 'M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z';
const D_USER   = 'M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z';

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const ESTADO_STYLE: Record<string, string> = {
  PENDIENTE: 'bg-amber-100 text-amber-700',
  PAGADO:    'bg-green-100 text-green-700',
  ANULADO:   'bg-slate-100 text-slate-500',
};

const sol = (n: number | null) =>
  n == null ? '—' :
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Modal de pago ─────────────────────────────────────────────
const ModalPago: React.FC<{
  titulo:           string;
  montoFinal:       number;
  tiposDescuento:   TipoDescuento[];
  descuentosAuto:   DescuentoAsignado[];
  registradoPorId:  number;
  onConfirm:        (p: PagarPayload) => Promise<void>;
  onClose:          () => void;
}> = ({ titulo, montoFinal, tiposDescuento, descuentosAuto, registradoPorId, onConfirm, onClose }) => {
  const [tipoDescuentoId, setTipoDescuentoId] = useState<string>('');
  const [observacion, setObservacion]         = useState('');
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState<string | null>(null);

  const handlePagar = async () => {
    setSaving(true); setError(null);
    try {
      await onConfirm({
        tipoDescuentoId:    tipoDescuentoId ? Number(tipoDescuentoId) : null,
        montoPersonalizado: null,
        registradoPorId,
        observacion:        observacion || undefined,
      });
    } catch (e: unknown) {
      if (e instanceof HttpError && e.status === 409) {
        setError('Alguien más procesó este pago. Recarga la página para ver el estado actualizado.');
      } else {
        setError(e instanceof Error ? e.message : 'Error al registrar pago');
      }
      setSaving(false);
    }
  };

  const descuentoOverride = tiposDescuento.find(t => String(t.idTipoDescuento) === tipoDescuentoId);

  // Calcular preview del monto con los descuentos automáticos aplicados
  const montoEstimado = React.useMemo(() => {
    const descuentosActivos = tipoDescuentoId ? [] : descuentosAuto; // si hay override, no aplicar los automáticos
    if (descuentosActivos.length === 0 && !descuentoOverride) return null;

    const descAplicar = descuentoOverride ? [descuentoOverride] : descuentosActivos;
    let monto = montoFinal;
    for (const d of descAplicar) {
      if (d.tipo === 'PORCENTAJE') {
        monto = monto * (1 - d.valor / 100);
      } else {
        monto = monto - d.valor;
      }
    }
    return Math.max(0, monto);
  }, [montoFinal, descuentosAuto, descuentoOverride, tipoDescuentoId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Ico d={D_MONEY} cls="w-4 h-4 text-green-700" />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm">Registrar Pago</p>
              <p className="text-xs text-slate-400">{titulo}</p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <Ico d={D_X} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Resumen de montos */}
          <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Tarifa base</span>
              <span className="font-semibold text-slate-700 tabular-nums">{sol(montoFinal)}</span>
            </div>
            {montoEstimado !== null && (
              <>
                {(tipoDescuentoId ? [descuentoOverride!] : descuentosAuto).map(d => (
                  <div key={d.idTipoDescuento} className="flex items-center justify-between">
                    <span className="text-xs text-teal-600">{d.nombre}</span>
                    <span className="text-xs font-semibold text-teal-600 tabular-nums">
                      -{d.tipo === 'PORCENTAJE' ? `${d.valor}%` : sol(d.valor)}
                    </span>
                  </div>
                ))}
                <div className="border-t border-slate-200 pt-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-700">Total estimado</span>
                  <span className="font-bold text-green-700 tabular-nums text-base">{sol(montoEstimado)}</span>
                </div>
              </>
            )}
            {montoEstimado === null && (
              <div className="border-t border-slate-200 pt-2 flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">Total</span>
                <span className="font-bold text-slate-800 tabular-nums text-base">{sol(montoFinal)}</span>
              </div>
            )}
          </div>

          {/* Descuentos automáticos asignados */}
          {descuentosAuto.length > 0 && !tipoDescuentoId && (
            <div className="flex flex-wrap gap-2">
              {descuentosAuto.map(d => (
                <span key={d.idTipoDescuento}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold
                             bg-teal-50 text-teal-700 border border-teal-200
                             px-2.5 py-1 rounded-full">
                  <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                    <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd"/>
                  </svg>
                  Automático
                </span>
              ))}
              <p className="w-full text-xs text-teal-600">
                El sistema aplica estos descuentos automáticamente al confirmar.
              </p>
            </div>
          )}

          {/* Sobrescribir descuento (opcional) */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
              Sobrescribir descuento <span className="font-normal text-slate-400">(opcional)</span>
            </label>
            <select value={tipoDescuentoId} onChange={e => setTipoDescuentoId(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700
                         outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white">
              <option value="">
                {descuentosAuto.length > 0 ? 'Usar descuentos automáticos' : 'Sin descuento'}
              </option>
              {tiposDescuento.filter(t => t.activo).map(t => (
                <option key={t.idTipoDescuento} value={t.idTipoDescuento}>
                  {t.nombre} — {t.tipo === 'PORCENTAJE' ? `${t.valor}%` : sol(t.valor)}
                </option>
              ))}
            </select>
            {descuentoOverride && (
              <p className="text-xs text-indigo-600">
                Tendrá precedencia sobre los descuentos automáticos:&nbsp;
                {descuentoOverride.tipo === 'PORCENTAJE'
                  ? `${descuentoOverride.valor}% sobre el monto`
                  : `${sol(descuentoOverride.valor)} de descuento`}
              </p>
            )}
          </div>

          {/* Observación */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
              Observación <span className="font-normal text-slate-400">(opcional)</span>
            </label>
            <textarea value={observacion} onChange={e => setObservacion(e.target.value)}
              rows={2} placeholder="Ej: Pago en efectivo"
              className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800
                         outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold
                       text-slate-600 hover:bg-slate-100 transition-colors">
            Cancelar
          </button>
          <button onClick={handlePagar} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-green-600 hover:bg-green-700
                       text-white text-sm font-semibold transition-colors disabled:opacity-60 shadow-sm">
            {saving ? <SpinnerIcon /> : <Ico d={D_CHECK} />}
            Confirmar pago
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Tarjeta PagoMatricula ─────────────────────────────────────
const CardPagoMatricula: React.FC<{
  pago:    PagoMatriculaResumen;
  onPagar: (p: PagoMatriculaResumen) => void;
}> = ({ pago, onPagar }) => {
  const pagado = pago.estado === 'PAGADO';
  return (
    <div className={`rounded-2xl border p-5
      ${pagado ? 'border-green-200 bg-green-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
            ${pagado ? 'bg-green-100' : 'bg-amber-100'}`}>
            <Ico d={D_MONEY} cls={`w-5 h-5 ${pagado ? 'text-green-600' : 'text-amber-600'}`} />
          </div>
          <div>
            <p className="font-bold text-slate-800 text-sm">Pago de Matrícula</p>
            {pago.descuentoNombre && (
              <p className="text-xs text-indigo-600 mt-0.5">Descuento: {pago.descuentoNombre}</p>
            )}
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap
          ${ESTADO_STYLE[pago.estado] ?? 'bg-slate-100 text-slate-600'}`}>
          {pago.estado}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/70 rounded-xl px-3 py-2">
          <p className="text-xs text-slate-400">Tarifa</p>
          <p className="font-semibold text-slate-700 text-sm tabular-nums">{sol(pago.montoTarifa)}</p>
        </div>
        <div className="bg-white/70 rounded-xl px-3 py-2">
          <p className="text-xs text-slate-400">Descuento</p>
          <p className="font-semibold text-slate-700 text-sm tabular-nums">{sol(pago.montoDescuento)}</p>
        </div>
        <div className="bg-white/70 rounded-xl px-3 py-2">
          <p className="text-xs text-slate-400">Total</p>
          <p className={`font-bold text-sm tabular-nums ${pagado ? 'text-green-700' : 'text-amber-700'}`}>
            {sol(pago.montoFinal)}
          </p>
        </div>
        <div className="bg-white/70 rounded-xl px-3 py-2">
          <p className="text-xs text-slate-400">Pagado</p>
          <p className="font-semibold text-slate-700 text-sm tabular-nums">{sol(pago.montoPagado)}</p>
        </div>
      </div>

      {pagado && pago.fechaPago && (
        <p className="mt-3 text-xs text-green-600">Pagado el {pago.fechaPago}</p>
      )}

      {!pagado && pago.estado !== 'ANULADO' && (
        <button onClick={() => onPagar(pago)}
          className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                     bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold
                     transition-colors shadow-sm">
          <Ico d={D_MONEY} />
          Registrar pago de matrícula
        </button>
      )}
    </div>
  );
};

// ── Lista Pensiones ───────────────────────────────────────────
const ListaPensiones: React.FC<{
  pensiones:       PensionItem[];
  onPagar:         (p: PensionItem) => void;
  onDuplicado:     (p: PensionItem) => void;
  onReemitir:      (p: PensionItem) => void;
  vencidasIds?:    Set<number>;
  reemitiendoId:   number | null;
  esRecuperacion?: boolean;
}> = ({ pensiones, onPagar, onDuplicado, onReemitir, vencidasIds, reemitiendoId, esRecuperacion }) => {
  if (pensiones.length === 0) {
    if (esRecuperacion) {
      return (
        <div className="text-center py-8 px-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 mb-3">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
              <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd"/>
            </svg>
          </div>
          <p className="text-sm font-semibold text-slate-700 m-0">Año de Recuperación</p>
          <p className="text-xs text-slate-500 mt-1">
            En este período solo se cobra la matrícula. No hay pensiones mensuales.
          </p>
        </div>
      );
    }
    return <div className="text-center py-8 text-slate-400 text-sm">No hay pensiones registradas</div>;
  }

  return (
    <div className="space-y-2">
      {pensiones.map(p => {
        const pagado  = p.estado === 'PAGADO';
        const anulado = p.estado === 'ANULADO';
        return (
          <div key={p.idPension}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all
              ${pagado  ? 'border-green-200 bg-green-50/30'
              : anulado ? 'border-slate-200 bg-slate-50 opacity-60'
              :           'border-slate-200 bg-white hover:border-blue-200'}`}>

            {/* Mes */}
            <div className={`w-14 rounded-xl flex flex-col items-center justify-center py-2
              flex-shrink-0 text-xs font-bold
              ${pagado ? 'bg-green-100 text-green-700'
              : anulado ? 'bg-slate-100 text-slate-400'
              :           'bg-blue-100 text-blue-700'}`}>
              <span className="text-sm leading-tight capitalize">{p.mesNombre.slice(0, 3)}</span>
              <span className="opacity-70 text-[10px]">{p.anioPension}</span>
            </div>

            {/* N° cuota */}
            <span className="text-xs text-slate-400 flex-shrink-0 w-12 text-center">
              #{p.numeroPension}
            </span>

            {/* Montos */}
            <div className="flex-1 min-w-0">
              <div className="grid grid-cols-2 gap-x-4 text-xs">
                <div>
                  <p className="text-slate-400">Total</p>
                  <p className="font-bold text-slate-800 tabular-nums">{sol(p.montoFinal)}</p>
                  {p.montoDescuento > 0 && (
                    <p className="text-teal-600 tabular-nums text-[10px]">
                      -{sol(p.montoDescuento)} dto.
                    </p>
                  )}
                </div>
                <div>
                  {pagado ? (
                    <>
                      <p className="text-slate-400">Pagado</p>
                      <p className="font-semibold text-green-700 tabular-nums">{sol(p.montoPagado)}</p>
                    </>
                  ) : p.fechaVencimiento ? (
                    <>
                      <p className="text-slate-400">Vence</p>
                      <p className="font-semibold text-slate-600">{p.fechaVencimiento}</p>
                    </>
                  ) : null}
                </div>
              </div>
              {p.descuentoNombre && (
                <p className="text-teal-600 text-[10px] mt-0.5 truncate">{p.descuentoNombre}</p>
              )}
            </div>

            {/* Estado / Acción */}
            <div className="flex-shrink-0">
              {pagado ? (
                <div className="flex flex-col items-end gap-1">
                  <span className="inline-flex items-center gap-1 text-xs font-bold
                                   text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    <Ico d={D_CHECK} cls="w-3 h-3" /> Pagado
                  </span>
                  {p.fechaPago && <span className="text-xs text-slate-400">{p.fechaPago}</span>}

                  {/* Comprobante SUNAT */}
                  {p.comprobanteEstado === 'EMITIDO' && p.comprobanteNumero && (
                    <span className="text-[10px] font-mono font-semibold text-emerald-700
                                     bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"
                          title="Comprobante emitido">
                      {p.comprobanteNumero}
                    </span>
                  )}
                  {p.comprobanteEstado === 'PENDIENTE' && (
                    <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-semibold">
                      Emitiendo...
                    </span>
                  )}
                  {p.comprobanteEstado === 'ERROR' && (
                    <button
                      onClick={() => onReemitir(p)}
                      disabled={reemitiendoId === p.idPension}
                      title="Error al emitir comprobante — haz clic para reintentar"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px]
                                 font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors
                                 disabled:opacity-60"
                    >
                      {reemitiendoId === p.idPension ? (
                        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                          <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd"/>
                        </svg>
                      )}
                      Reemitir
                    </button>
                  )}
                  {p.comprobanteEstado === 'ANULADO' && (
                    <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-semibold">
                      Comprobante anulado
                    </span>
                  )}

                  <button onClick={() => onDuplicado(p)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px]
                               font-semibold bg-slate-700 hover:bg-slate-800 text-white transition-colors">
                    <Ico d={D_PRINT} cls="w-3 h-3" />
                    Duplicado
                  </button>
                </div>
              ) : anulado ? (
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                  Anulado
                </span>
              ) : vencidasIds?.has(p.idPension) ? (
                <span className="flex flex-col items-end gap-0.5">
                  <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                    Período vencido
                  </span>
                  <span className="text-[10px] text-slate-400 max-w-[120px] text-right">Pago fuera del período de edición</span>
                </span>
              ) : (
                <button onClick={() => onPagar(p)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                             bg-blue-700 hover:bg-blue-800 text-white transition-colors">
                  <Ico d={D_MONEY} cls="w-3.5 h-3.5" />
                  Cobrar
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Caja de estadística ───────────────────────────────────────
const StatBox = ({ label, value, color = 'slate' }: { label: string; value: string; color?: string }) => {
  const colors: Record<string, string> = {
    slate:  'bg-slate-50  text-slate-700',
    green:  'bg-green-50  text-green-700',
    amber:  'bg-amber-50  text-amber-700',
    blue:   'bg-blue-50   text-blue-700',
  };
  return (
    <div className={`rounded-xl px-3 py-2.5 text-center ${colors[color]}`}>
      <p className="text-xs opacity-70 leading-tight">{label}</p>
      <p className="font-bold text-sm mt-0.5 tabular-nums">{value}</p>
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

// ── Modal Ticket ──────────────────────────────────────────────
const MESES_TICKET = [
  '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const D_PRINT    = 'M5 2.75C5 1.784 5.784 1 6.75 1h6.5c.966 0 1.75.784 1.75 1.75v3.552c.377.046.752.097 1.126.153A2.212 2.212 0 0 1 18 8.653v4.097A2.25 2.25 0 0 1 15.75 15h-.241l.305 1.984A1.75 1.75 0 0 1 14.084 19H5.915a1.75 1.75 0 0 1-1.73-2.016L4.49 15H4.25A2.25 2.25 0 0 1 2 12.75V8.653c0-1.082.775-2.034 1.874-2.198.374-.056.749-.107 1.126-.153V2.75Zm8.5 3.397a41.533 41.533 0 0 0-7 0V2.75a.25.25 0 0 1 .25-.25h6.5a.25.25 0 0 1 .25.25v3.397ZM6.608 12.5a.25.25 0 0 0-.247.212l-.693 4.5a.25.25 0 0 0 .247.288h8.17a.25.25 0 0 0 .246-.288l-.692-4.5a.25.25 0 0 0-.247-.212H6.608Z';
const D_REFRESH  = 'M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z';
const D_DOWNLOAD = 'M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75ZM3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z';

const ModalTicket: React.FC<{ ticket: TicketData; duplicado?: boolean; onClose: () => void }> = ({ ticket, duplicado = false, onClose }) => {
  const solT = (n: number) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);
  const [descargando, setDescargando] = useState(false);

  const handleDescargar = async () => {
    setDescargando(true);
    try { await descargarTicket(ticket, duplicado); }
    finally { setDescargando(false); }
  };

  const tipoLabel = ticket.tipo === 'MATRICULA'
    ? `Matrícula ${ticket.anio}`
    : `Pensión ${ticket.numeroPension}° – ${MESES_TICKET[ticket.mes ?? 0]} ${ticket.anio}`;

  // Intenta imprimir automáticamente al abrir el modal
  useEffect(() => {
    const win = imprimirTicket(ticket, duplicado);
    if (!win) {
      // popup bloqueado — los botones están visibles como fallback
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(15,23,42,0.6)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center
              ${duplicado ? 'bg-slate-200' : 'bg-green-100'}`}>
              <svg viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 ${duplicado ? 'text-slate-600' : 'text-green-700'}`}>
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm">
                {duplicado ? 'Duplicado de boleta' : 'Pago registrado'}
              </p>
              <p className="text-xs text-slate-400">{tipoLabel}</p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
            <Ico d={D_X} />
          </button>
        </div>

        {/* Vista previa del ticket */}
        <div className="px-5 pt-4 pb-2 flex justify-center">
          <div className="w-60 border border-dashed border-slate-300 rounded-lg p-4
                          font-mono text-xs text-slate-800 leading-snug">
            {duplicado && (
              <p className="text-center font-bold text-[10px] tracking-widest text-slate-500 mb-1">
                — DUPLICADO —
              </p>
            )}
            <p className="text-center font-bold text-sm leading-tight break-words">
              {ticket.institucionNombre || 'Institución Educativa'}
            </p>
            <p className="text-center text-slate-400 text-[10px]">Sistema de Administración Escolar</p>
            <div className="border-t border-dashed border-slate-300 my-2" />

            <div className="flex justify-center mb-1">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded
                ${ticket.tipo === 'MATRICULA' ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white'}`}>
                {ticket.tipo === 'MATRICULA' ? 'MATRÍCULA' : 'PENSIÓN'}
              </span>
            </div>
            <p className="text-center font-bold">{tipoLabel}</p>

            <div className="border-t border-dashed border-slate-300 my-2" />
            <p className="font-bold leading-tight">
              {ticket.estudianteApellidos}, {ticket.estudianteNombres}
            </p>
            <p>DNI: {ticket.estudianteDni}</p>
            <p>{ticket.gradoNombre} – Sec. {ticket.seccionNombre}</p>
            <p className="text-slate-400 text-[10px]">{ticket.gradoNivel} · {ticket.anio}</p>

            <div className="border-t border-dashed border-slate-300 my-2" />
            <div className="flex justify-between">
              <span className="text-slate-500">Tarifa base</span>
              <span>{solT(ticket.montoBase)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Descuento</span>
              <span>{ticket.descuento > 0 ? `- ${solT(ticket.descuento)}` : solT(0)}</span>
            </div>
            {ticket.descuentoNombre && (
              <p className="text-slate-400 text-[10px]">· {ticket.descuentoNombre}</p>
            )}
            <div className="border-t border-dashed border-slate-300 my-2" />
            <div className="flex justify-between font-bold text-sm">
              <span>TOTAL</span>
              <span className="text-green-700">{solT(ticket.montoPagado)}</span>
            </div>
            <div className="border-t border-dashed border-slate-300 my-2" />
            <div className="flex justify-between">
              <span className="text-slate-500">Fecha</span>
              <span>{ticket.fecha}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Cajero</span>
              <span className="truncate max-w-[110px] text-right">{ticket.cajeroNombre}</span>
            </div>
            {ticket.observacion && (
              <div className="flex justify-between">
                <span className="text-slate-500">Obs.</span>
                <span className="truncate max-w-[110px] text-right">{ticket.observacion}</span>
              </div>
            )}
            <div className="border-t border-dashed border-slate-300 my-2" />
            <p className="text-center text-slate-400 text-[10px]">¡Gracias por su pago!</p>
          </div>
        </div>

        {/* Acciones */}
        <div className="px-5 pb-5 space-y-2">
          <p className="text-xs text-slate-400 text-center">
            Se abrió la ventana de impresión automáticamente.
            Si no tienes impresora, descarga el ticket.
          </p>
          <div className="flex gap-2">
            <button onClick={() => imprimirTicket(ticket, duplicado)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                         bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold transition-colors">
              <Ico d={D_PRINT} />
              Imprimir
            </button>
            <button onClick={handleDescargar} disabled={descargando}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                         bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60
                         text-white text-sm font-semibold transition-colors">
              {descargando ? <SpinnerIcon /> : <Ico d={D_DOWNLOAD} />}
              {descargando ? 'Generando…' : 'Descargar PDF'}
            </button>
          </div>
          <button onClick={onClose}
            className="w-full py-2 rounded-xl border border-slate-200 text-sm font-semibold
                       text-slate-600 hover:bg-slate-50 transition-colors">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Cobros adicionales en estado de cuenta ────────────────────
const CobrosAdicionalesCard: React.FC<{
  cobros:          CobroAdicionalResumen[];
  registradoPorId: number;
  onPagado:        () => void;
}> = ({ cobros, registradoPorId, onPagado }) => {
  const [pagandoId,  setPagandoId]  = useState<number | null>(null);
  const [errorPago,  setErrorPago]  = useState<string | null>(null);

  const handlePagar = async (idCobro: number) => {
    setPagandoId(idCobro); setErrorPago(null);
    try {
      await cobroAdicionalService.pagar(idCobro, { registradoPorId });
      onPagado();
    } catch (e: unknown) {
      setErrorPago(e instanceof Error ? e.message : 'Error al registrar pago');
    } finally { setPagandoId(null); }
  };

  const pendientes = cobros.filter(c => c.estado === 'PENDIENTE');

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-bold text-slate-700 text-sm">Cobros adicionales</h2>
        {pendientes.length > 0 && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
            {pendientes.length} pendiente{pendientes.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="divide-y divide-slate-50">
        {cobros.map(c => (
          <div key={c.idCobro} className="flex items-center gap-4 px-5 py-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 text-sm truncate">{c.conceptoNombre}</p>
              <p className="text-xs text-indigo-600">{c.categoriaNombre}</p>
              {c.fechaVencimiento && <p className="text-xs text-slate-400">Vence: {c.fechaVencimiento}</p>}
            </div>
            <p className="font-bold tabular-nums text-slate-800 whitespace-nowrap text-sm">
              {sol(c.monto)}
            </p>
            <div className="flex-shrink-0">
              {c.estado === 'PENDIENTE' ? (
                <button
                  onClick={() => handlePagar(c.idCobro)}
                  disabled={pagandoId === c.idCobro}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                             bg-green-50 text-green-700 hover:bg-green-100 border border-green-200
                             transition-colors disabled:opacity-60 whitespace-nowrap">
                  {pagandoId === c.idCobro
                    ? <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/></svg>
                    : <Ico d={D_MONEY} cls="w-3.5 h-3.5" />}
                  Cobrar
                </button>
              ) : (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                  ${c.estado === 'PAGADO' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
                  {c.estado}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      {errorPago && (
        <div className="px-5 py-2 bg-red-50 border-t border-red-200 text-xs text-red-600">{errorPago}</div>
      )}
    </div>
  );
};

type ModalTarget =
  | { tipo: 'matricula'; data: PagoMatriculaResumen }
  | { tipo: 'pension';   data: PensionItem };

const CobrosPage: React.FC = () => {
  const { user } = useAuth();

  const [añosEscolares, setAñosEscolares]   = useState<AñoEscolar[]>([]);
  const [anioId, setAnioId]                 = useState<number | ''>('');
  const [tiposDescuento, setTiposDescuento] = useState<TipoDescuento[]>([]);

  const [query, setQuery]           = useState('');
  const [buscando, setBuscando]     = useState(false);
  const [resultados, setResultados] = useState<UsuarioListItem[]>([]);
  const [estudiante, setEstudiante] = useState<UsuarioListItem | null>(null);

  const [cuenta, setCuenta]               = useState<EstadoCuenta | null>(null);
  const [loadingCuenta, setLoadingCuenta] = useState(false);
  const [errorCuenta, setErrorCuenta]     = useState<string | null>(null);

  const [modal, setModal]   = useState<ModalTarget | null>(null);
  const idempotencyKeyRef   = React.useRef('');
  const [pensionesVencidas,  setPensionesVencidas]  = useState<Set<number>>(new Set());
  const [reemitiendoId,      setReemitiendoId]      = useState<number | null>(null);
  const [errorReemision,     setErrorReemision]      = useState<string | null>(null);

  const handleReemitir = async (p: PensionItem) => {
    setReemitiendoId(p.idPension); setErrorReemision(null);
    try {
      await pensionService.reemitirComprobante(p.idPension);
      if (estudiante && anioId) cargarCuenta(estudiante, anioId);
    } catch (e: unknown) {
      setErrorReemision(e instanceof Error ? e.message : 'Error al reemitir el comprobante');
    } finally {
      setReemitiendoId(null);
    }
  };
  const [ticket, setTicket]           = useState<TicketData | null>(null);
  const [ticketDuplicado, setTicketDuplicado] = useState(false);

  useEffect(() => {
    Promise.all([
      catalogoService.getAñosEscolares(),
      tipoDescuentoService.listar(),
    ]).then(([años, tipos]) => {
      const añosList = Array.isArray(años) ? años : [];
      setAñosEscolares(añosList);
      if (añosList.length > 0) setAnioId(añosList[0].idAnioEscolar);
      setTiposDescuento(Array.isArray(tipos) ? tipos : []);
    }).catch(() => {});
  }, []);

  const buscar = useCallback(async () => {
    if (!query.trim()) return;
    setBuscando(true);
    try {
      const data = await usuarioService.listarEstudiantes({ search: query });
      setResultados(Array.isArray(data) ? data : []);
    } catch { setResultados([]); }
    finally { setBuscando(false); }
  }, [query]);

  const cargarCuenta = useCallback(async (est: UsuarioListItem, año: number | '') => {
    if (!año) return;
    setLoadingCuenta(true); setErrorCuenta(null); setCuenta(null);
    try {
      const data = await estadoCuentaService.getByEstudiante(est.idUsuario, año as number);
      setCuenta(data);
    } catch (e: unknown) {
      setErrorCuenta(e instanceof Error ? e.message : 'Error al cargar estado de cuenta');
    } finally { setLoadingCuenta(false); }
  }, []);

  const seleccionarEstudiante = (u: UsuarioListItem) => {
    setEstudiante(u);
    setResultados([]);
    setQuery('');
    if (anioId) cargarCuenta(u, anioId);
  };

  const handleAnioChange = (id: number | '') => {
    setAnioId(id);
    if (estudiante && id) cargarCuenta(estudiante, id);
  };

  const resolverDescuento = (payload: PagarPayload) =>
    payload.tipoDescuentoId
      ? tiposDescuento.find(t => t.idTipoDescuento === payload.tipoDescuentoId)
      : undefined;

  const pagarMatricula = async (payload: PagarPayload) => {
    if (!modal || modal.tipo !== 'matricula' || !cuenta || !anioId) return;
    // Capturar respuesta del backend — contiene montos reales post-descuento
    const result    = await pagoMatriculaService.pagar(cuenta.estudianteId, anioId as number, payload, idempotencyKeyRef.current);
    const override  = resolverDescuento(payload);
    const autoDesc  = cuenta.descuentos.find(d => d.aplicaMatricula);
    setTicketDuplicado(false);
    setTicket({
      tipo:                'MATRICULA',
      institucionNombre:   getConfig().institucionNombre || 'Institución Educativa',
      estudianteNombres:   cuenta.estudianteNombres,
      estudianteApellidos: cuenta.estudianteApellidos,
      estudianteDni:       cuenta.estudianteDni,
      anio:                cuenta.anio,
      gradoNombre:         cuenta.gradoNombre,
      gradoNivel:          cuenta.nivel,
      seccionNombre:       cuenta.seccionNombre,
      montoBase:           result.montoTarifa,
      descuento:           result.montoDescuento,
      montoFinal:          result.montoFinal,
      montoPagado:         result.montoPagado ?? result.montoFinal,
      descuentoNombre:     override?.nombre ?? autoDesc?.nombre ?? null,
      observacion:         payload.observacion || null,
      cajeroNombre:        user?.getNombreCompleto() ?? 'Sistema',
      fecha:               new Date().toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' }),
    });
    setModal(null);
    if (estudiante && anioId) cargarCuenta(estudiante, anioId);
  };

  const pagarPension = async (payload: PagarPayload) => {
    if (!modal || modal.tipo !== 'pension' || !cuenta || !anioId) return;
    let result;
    try {
      result = await pensionService.pagar(cuenta.estudianteId, modal.data.numeroPension, anioId as number, payload, idempotencyKeyRef.current);
    } catch (err: unknown) {
      // Detectar error de cierre contable → deshabilitar botón para esa pensión
      if (err instanceof Error && err.message.includes('período de edición')) {
        setPensionesVencidas(prev => new Set(prev).add(modal.data.idPension));
      }
      throw err; // re-lanzar para que ModalPago lo muestre
    }
    const override  = resolverDescuento(payload);
    const autoDesc  = cuenta.descuentos.find(d => d.aplicaPension);
    setTicketDuplicado(false);
    setTicket({
      tipo:                'PENSION',
      institucionNombre:   getConfig().institucionNombre || 'Institución Educativa',
      estudianteNombres:   cuenta.estudianteNombres,
      estudianteApellidos: cuenta.estudianteApellidos,
      estudianteDni:       cuenta.estudianteDni,
      anio:                cuenta.anio,
      gradoNombre:         cuenta.gradoNombre,
      gradoNivel:          cuenta.nivel,
      seccionNombre:       cuenta.seccionNombre,
      montoBase:           result.montoTarifa,
      descuento:           result.montoDescuento,
      montoFinal:          result.montoFinal,
      montoPagado:         result.montoPagado ?? result.montoFinal,
      descuentoNombre:     override?.nombre ?? autoDesc?.nombre ?? null,
      observacion:         payload.observacion || null,
      mes:                 result.mes,
      numeroPension:       modal.data.numeroPension,
      cajeroNombre:        user?.getNombreCompleto() ?? 'Sistema',
      fecha:               new Date().toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' }),
    });
    setModal(null);
    if (estudiante && anioId) cargarCuenta(estudiante, anioId);
  };

  const handleDuplicadoPension = (p: PensionItem) => {
    if (!cuenta) return;
    setTicketDuplicado(true);
    setTicket({
      tipo:                'PENSION',
      institucionNombre:   getConfig().institucionNombre || 'Institución Educativa',
      estudianteNombres:   cuenta.estudianteNombres,
      estudianteApellidos: cuenta.estudianteApellidos,
      estudianteDni:       cuenta.estudianteDni,
      anio:                cuenta.anio,
      gradoNombre:         cuenta.gradoNombre,
      gradoNivel:          cuenta.nivel,
      seccionNombre:       cuenta.seccionNombre,
      montoBase:           p.montoTarifa,
      descuento:           p.montoDescuento,
      montoFinal:          p.montoFinal,
      montoPagado:         p.montoPagado ?? p.montoFinal,
      descuentoNombre:     p.descuentoNombre,
      observacion:         p.observacion,
      mes:                 p.mes,
      numeroPension:       p.numeroPension,
      cajeroNombre:        user?.getNombreCompleto() ?? 'Sistema',
      fecha:               p.fechaPago ?? new Date().toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' }),
    });
  };

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Cobros</h1>
          <p className="text-slate-400 text-sm mt-0.5">Registro de pagos de matrícula y pensiones</p>
        </div>
        <select value={anioId}
          onChange={e => handleAnioChange(e.target.value ? Number(e.target.value) : '')}
          className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm
                     text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]
                     self-start sm:self-auto">
          {añosEscolares.map(a => (
            <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
          ))}
        </select>
      </div>

      {/* Búsqueda */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Buscar estudiante</p>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Ico d={D_SEARCH} />
            </span>
            <input type="text" placeholder="Nombre, apellido o DNI..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscar()}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                         text-sm text-slate-800 placeholder-slate-300 outline-none
                         focus:ring-2 focus:ring-blue-500 transition-all" />
          </div>
          <button onClick={buscar} disabled={buscando || !query.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                       text-white text-sm font-semibold transition-colors disabled:opacity-60">
            {buscando ? <SpinnerIcon /> : <Ico d={D_SEARCH} />}
            Buscar
          </button>
        </div>

        {resultados.length > 0 && (
          <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
            {resultados.map(u => (
              <button key={u.idUsuario} onClick={() => seleccionarEstudiante(u)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-xs
                                flex items-center justify-center flex-shrink-0">
                  {u.nombres.charAt(0)}{u.apellidos.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">
                    {u.apellidos}, {u.nombres}
                  </p>
                  <p className="text-xs text-slate-400">DNI: {u.dni}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Estado cuenta */}
      {estudiante && (
        <div className="space-y-4">

          {/* Cabecera estudiante */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4
                          flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 font-bold
                              flex items-center justify-center text-sm flex-shrink-0">
                {estudiante.nombres.charAt(0)}{estudiante.apellidos.charAt(0)}
              </div>
              <div>
                <p className="font-bold text-slate-800">
                  {estudiante.apellidos}, {estudiante.nombres}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-400">DNI: {estudiante.dni}</span>
                  {cuenta && (
                    <>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                        ${NIVEL_COLOR[cuenta.nivel] ?? 'bg-slate-100 text-slate-600'}`}>
                        {cuenta.nivel}
                      </span>
                      <span className="text-xs text-slate-500">
                        {cuenta.gradoNombre} — Sección {cuenta.seccionNombre}
                      </span>
                      <span className="text-xs text-slate-400">· {cuenta.anio}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => cargarCuenta(estudiante, anioId)}
                disabled={loadingCuenta || !anioId}
                title="Actualizar estado de cuenta"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold
                           text-slate-600 bg-slate-100 hover:bg-blue-100 hover:text-blue-700
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg viewBox="0 0 20 20" fill="currentColor"
                     className={`w-4 h-4 ${loadingCuenta ? 'animate-spin' : ''}`}>
                  <path d={D_REFRESH} />
                </svg>
                <span className="hidden sm:inline">Actualizar</span>
              </button>
              <button onClick={() => { setEstudiante(null); setCuenta(null); }}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <Ico d={D_X} />
              </button>
            </div>
          </div>

          {loadingCuenta ? (
            <div className="flex justify-center py-12"><SpinnerIcon /></div>
          ) : errorCuenta ? (
            <div className="bg-red-50 rounded-2xl border border-red-200 p-5 text-red-600 text-sm">
              {errorCuenta}
            </div>
          ) : cuenta ? (
            <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

              {/* Columna izquierda */}
              <div className="space-y-4">

                {/* Pago matrícula */}
                {cuenta.pagoMatricula ? (
                  <CardPagoMatricula
                    pago={cuenta.pagoMatricula}
                    onPagar={p => { idempotencyKeyRef.current = crypto.randomUUID(); setModal({ tipo: 'matricula', data: p }); }}
                  />
                ) : (
                  <div className="rounded-2xl border border-slate-200 p-5 text-center text-slate-400 text-sm">
                    Sin pago de matrícula registrado
                  </div>
                )}

                {/* Resumen del backend */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                    Resumen pensiones
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <StatBox label="Pagadas"    value={String(cuenta.resumen.cuotasPagadas)}    color="green" />
                    <StatBox label="Pendientes" value={String(cuenta.resumen.cuotasPendientes)} color="amber" />
                    <StatBox label="Por cobrar" value={sol(cuenta.resumen.totalPendiente)}      color="blue" />
                    <StatBox label="Total pagado" value={sol(cuenta.resumen.totalPagado)}       color="slate" />
                  </div>
                  {/* Barra de progreso */}
                  {(cuenta.resumen.cuotasPagadas + cuenta.resumen.cuotasPendientes) > 0 && (
                    <div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all"
                          style={{
                            width: `${(cuenta.resumen.cuotasPagadas /
                              (cuenta.resumen.cuotasPagadas + cuenta.resumen.cuotasPendientes)) * 100}%`
                          }} />
                      </div>
                      <p className="text-xs text-slate-400 mt-1 text-right">
                        {Math.round((cuenta.resumen.cuotasPagadas /
                          (cuenta.resumen.cuotasPagadas + cuenta.resumen.cuotasPendientes)) * 100)}% pagado
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Columna derecha: pensiones */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="font-bold text-slate-700 text-sm">Pensiones mensuales</h2>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">{cuenta.pensiones.length} cuotas</span>
                    <button
                      onClick={() => cargarCuenta(estudiante!, anioId)}
                      disabled={loadingCuenta || !anioId}
                      title="Actualizar estado de cuenta"
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                 text-slate-500 hover:text-blue-700 hover:bg-blue-50
                                 border border-slate-200 hover:border-blue-200
                                 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor"
                           className={`w-3.5 h-3.5 ${loadingCuenta ? 'animate-spin' : ''}`}>
                        <path d={D_REFRESH} />
                      </svg>
                      Actualizar
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <ListaPensiones
                    pensiones={cuenta.pensiones}
                    onPagar={p => { idempotencyKeyRef.current = crypto.randomUUID(); setModal({ tipo: 'pension', data: p }); }}
                    onDuplicado={handleDuplicadoPension}
                    onReemitir={handleReemitir}
                    vencidasIds={pensionesVencidas}
                    reemitiendoId={reemitiendoId}
                    esRecuperacion={añosEscolares.find(a => a.idAnioEscolar === anioId)?.fase === 'RECUPERACION'}
                  />
                  {errorReemision && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">
                      {errorReemision}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cobros adicionales */}
            {cuenta.cobrosAdicionales && cuenta.cobrosAdicionales.length > 0 && (
              <CobrosAdicionalesCard
                cobros={cuenta.cobrosAdicionales}
                registradoPorId={user?.idUsuario ?? 0}
                onPagado={() => cargarCuenta(estudiante!, anioId)}
              />
            )}
            </div>
          ) : null}
        </div>
      )}

      {/* Estado vacío */}
      {!estudiante && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <Ico d={D_USER} cls="w-7 h-7 text-blue-400" />
          </div>
          <p className="text-slate-500 font-semibold">Busca un estudiante</p>
          <p className="text-slate-300 text-sm mt-1">
            Escribe el nombre, apellido o DNI para ver su estado de cuenta
          </p>
        </div>
      )}

      {/* Modal ticket post-pago */}
      {ticket && (
        <ModalTicket ticket={ticket} duplicado={ticketDuplicado}
          onClose={() => { setTicket(null); setTicketDuplicado(false); }} />
      )}

      {/* Modal de pago */}
      {modal && user && (
        <ModalPago
          titulo={
            modal.tipo === 'matricula'
              ? 'Matrícula'
              : `Pensión ${modal.data.mesNombre} ${modal.data.anioPension}`
          }
          montoFinal={modal.data.montoFinal}
          tiposDescuento={tiposDescuento}
          descuentosAuto={
            modal.tipo === 'matricula'
              ? (cuenta?.descuentos ?? []).filter(d => d.aplicaMatricula)
              : (cuenta?.descuentos ?? []).filter(d => d.aplicaPension)
          }
          registradoPorId={user.idUsuario}
          onConfirm={modal.tipo === 'matricula' ? pagarMatricula : pagarPension}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default CobrosPage;
